import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { BarChart3 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
        toast.error("Failed to load statistics");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="dark-spinner" />
      </div>
    );
  }

  if (!stats || stats.total_rounds === 0) {
    return (
      <div className="dark-page max-w-4xl mx-auto animate-fade-in p-4" data-testid="stats-page">
        <div className="dark-card border-dashed border-2 border-[var(--ps-border)] p-12 text-center">
          <BarChart3 className="w-16 h-16 text-[var(--ps-text-muted)] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[var(--ps-text-primary)] mb-2">No data yet</h3>
          <p className="text-[var(--ps-text-secondary)] mb-6">Play some rounds to see your statistics.</p>
          <NavLink to="/add-score">
            <Button className="dark-btn-primary">Add Your First Score</Button>
          </NavLink>
        </div>
      </div>
    );
  }

  const scoreDistribution = [
    { name: 'Under Par', value: stats.scores_vs_par.under_par || 0, color: 'var(--ps-success)' },
    { name: 'At Par', value: stats.scores_vs_par.at_par || 0, color: 'var(--ps-info)' },
    { name: 'Bogey (+1-5)', value: stats.scores_vs_par.bogey || 0, color: 'var(--ps-warning)' },
    { name: 'Double+ (+6)', value: stats.scores_vs_par.double_plus || 0, color: 'var(--ps-error)' },
  ].filter(d => d.value > 0);

  return (
    <div className="dark-page max-w-4xl mx-auto animate-fade-in p-4" data-testid="stats-page">

      {/* Key Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="dark-card p-4">
          <p className="text-sm text-[var(--ps-text-secondary)] mb-1">Rounds Played</p>
          <p className="text-2xl font-mono font-bold text-[var(--ps-text-primary)]">{stats.total_rounds}</p>
        </div>
        <div className="dark-card p-4">
          <p className="text-sm text-[var(--ps-text-secondary)] mb-1">Average</p>
          <p className="text-2xl font-mono font-bold text-[var(--ps-text-primary)]">{stats.average_score}</p>
        </div>
        <div className="dark-card p-4">
          <p className="text-sm text-[var(--ps-text-secondary)] mb-1">Best</p>
          <p className="text-2xl font-mono font-bold text-[var(--ps-primary)]">{stats.best_score}</p>
        </div>
        <div className="dark-card p-4">
          <p className="text-sm text-[var(--ps-text-secondary)] mb-1">Handicap</p>
          <p data-testid="stats-handicap-index" className="text-2xl font-mono font-bold text-[var(--ps-text-primary)]">{typeof stats.handicap === 'number' && Number.isFinite(stats.handicap) ? stats.handicap.toFixed(1) : 'N/A'}</p>
        </div>
      </div>

      {/* Trend Chart */}
      {stats.recent_trend?.length > 1 && (
        <div className="dark-card mb-8">
          <div className="p-4 border-b border-[var(--ps-border)]">
            <h3 className="text-lg font-bold text-[var(--ps-text-primary)]">Performance Trend</h3>
            <p className="text-sm text-[var(--ps-text-secondary)]">Your recent scores over time</p>
          </div>
          <div className="p-4">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.recent_trend}>
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => format(new Date(value), 'MMM d')}
                    stroke="var(--ps-text-muted)"
                    fontSize={12}
                  />
                  <YAxis 
                    domain={['dataMin - 5', 'dataMax + 5']}
                    stroke="var(--ps-text-muted)"
                    fontSize={12}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'var(--ps-bg-surface)', 
                      border: '1px solid var(--ps-border)',
                      borderRadius: '8px',
                      color: 'var(--ps-text-primary)'
                    }}
                    formatter={(value, name, props) => [
                      <span key="score" style={{color: 'var(--ps-text-primary)'}}>
                        {value} ({value - props.payload.par >= 0 ? '+' : ''}{value - props.payload.par})
                      </span>,
                      'Score'
                    ]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0]) {
                        return `${format(new Date(label), 'MMM d, yyyy')} - ${payload[0].payload.course}`;
                      }
                      return format(new Date(label), 'MMM d, yyyy');
                    }}
                    labelStyle={{color: 'var(--ps-text-secondary)'}}
                  />
                  <ReferenceLine y={72} stroke="var(--ps-primary)" strokeDasharray="3 3" />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="var(--ps-primary)" 
                    strokeWidth={3}
                    dot={{ fill: 'var(--ps-primary)', strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7, fill: 'var(--ps-primary-light)' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Score Distribution */}
      <div className="dark-card">
        <div className="p-4 border-b border-[var(--ps-border)]">
          <h3 className="text-lg font-bold text-[var(--ps-text-primary)]">Score Distribution</h3>
          <p className="text-sm text-[var(--ps-text-secondary)]">How your rounds compare to par</p>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {scoreDistribution.map((item) => (
              <div key={item.name} className="flex items-center gap-4">
                <div className="w-24 text-sm font-medium text-[var(--ps-text-secondary)]">{item.name}</div>
                <div className="flex-1 bg-[var(--ps-bg-elevated)] rounded-full h-4 overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${(item.value / stats.total_rounds) * 100}%`,
                      backgroundColor: item.color 
                    }}
                  />
                </div>
                <div className="w-12 text-right text-sm font-mono text-[var(--ps-text-muted)]">
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsPage;
